/**
 * Intenção genérica de "melhor <prato/doce/produto> de <zona>" (módulo puro).
 *
 * NÃO existe catálogo: qualquer produto nomeado pela pessoa é aceite tal como
 * foi escrito ("bacalhau", "sushi", "doce tradicional", "croissant", "gelato"),
 * em PT/EN/ES/FR/IT/DE. A zona também é livre — aldeia, vila, cidade, região
 * ou país — e é sempre devolvida como texto para ser resolvida pela geometria
 * já existente. Este módulo não decide nada sobre resultados: só lê o pedido.
 */

const W = "(?<![\\p{L}])";
const Wend = "(?![\\p{L}])";

const SUPERLATIVE = new RegExp(
  `${W}(melhor(?:es)?|mejor(?:es)?|miglior(?:e|i)?|meilleur(?:e|s|es)?|beste[nr]?|best|top\\s*\\d*)${Wend}`,
  "iu",
);

/** Palavras que anunciam o produto sem serem o produto. */
const CARRIER = new RegExp(
  `^(?:prato|pratos|plato|platos|piatto|piatti|plat|plats|dish|dishes|doce|doces|dulce|dulces|dolce|dolci|s[uü][sß]igkeit(?:en)?|sobremesa|sobremesas|dessert|desserts|pastel|past[eé]is|pastry|pastries|petisco|petiscos|tapa|tapas|comida|food|cozinha|cuisine|restaurante|restaurant|restaurantes|restaurants|sitio|s[ií]tio|lugar|local|locais|place|places)${Wend}\\s*`,
  "iu",
);

const AREA_LEAD =
  "(?:d[oa]s?|de|do|em|n[oa]s?|in|at|near|around|perto\\s+de|dans|en|del|della|di|aus|von|im)";

const NEAR_ME = new RegExp(
  `(perto\\s+de\\s+mim|perto\\s+daqui|aqui\\s+perto|cerca\\s+de\\s+m[ií]|near\\s+me|nearby|around\\s+me|pr[eè]s\\s+de\\s+moi|in\\s+der\\s+n[aä]he|vicino\\s+a\\s+me)`,
  "iu",
);

/** Termos que classificam o pedido como doçaria (muda a query de pesquisa). */
const SWEET_HINT = new RegExp(
  `${W}(doce|doces|do[cç]aria|sobremesa\\w*|pastel|past[eé]is|pastelaria\\w*|confeitaria\\w*|bolo\\w*|dulce\\w*|dolc[ei]|dessert\\w*|pastry|pastries|cake\\w*|s[uü][sß]igkeit\\w*|geb[aä]ck|g[eé]lat\\w*|gelat\\w*|ice\\s?cream|sweet\\w*)${Wend}`,
  "iu",
);

export type DishIntent = {
  /** Produto pedido, tal como escrito (sem palavras-veículo). */
  dish: string;
  /** Zona escrita no pedido, quando existe. */
  area: string | null;
  nearMe: boolean;
  /** Pedido de doçaria (usa pastelarias/confeitarias na pesquisa). */
  sweet: boolean;
};

const clean = (v: string) =>
  v
    .replace(/[!?.,;:]+\s*$/u, "")
    .replace(/\s+/gu, " ")
    .trim();

/** Ruído que nunca faz parte do produto nem da zona. */
const NOISE = new RegExp(
  `${W}(quero|queria|gostava|procuro|diz-?me|indica|qual|quais|onde|which|what|where|i\\s+want|show\\s+me|quiero|voglio|je\\s+veux|ich\\s+m[oö]chte|por\\s+favor|please)${Wend}`,
  "giu",
);

/**
 * Deteta "melhor X de Y" de forma genérica. Devolve `null` quando não há
 * superlativo ou quando não sobra produto nenhum depois do superlativo.
 */
export function detectDishIntent(text: string): DishIntent | null {
  const raw = clean(text ?? "");
  if (!raw) return null;
  const sup = SUPERLATIVE.exec(raw);
  if (!sup) return null;

  const nearMe = NEAR_ME.test(raw);
  let tail = clean(raw.slice((sup.index ?? 0) + sup[0].length));
  if (nearMe) tail = clean(tail.replace(NEAR_ME, " "));
  tail = clean(tail.replace(NOISE, " "));
  if (!tail) return null;

  // Remove palavras-veículo repetidas ("prato de bacalhau", "doce tradicional").
  let head = tail;
  let guard = 0;
  while (guard++ < 3) {
    const stripped = head.replace(CARRIER, "");
    const noLead = stripped.replace(new RegExp(`^(?:de|of|d[oa]s?|di|du|des)${Wend}\\s*`, "iu"), "");
    if (noLead === head) break;
    head = clean(noLead);
  }

  // Separa produto e zona: "sushi do norte de Portugal" → sushi | norte de Portugal.
  let dish = head;
  let area: string | null = null;
  const split = new RegExp(
    `^([\\p{L}\\p{M}'’-]+(?:\\s+[\\p{L}\\p{M}'’-]+){0,3}?)\\s+${AREA_LEAD}\\s+(.+)$`,
    "iu",
  ).exec(head);
  if (split) {
    dish = clean(split[1]);
    area = clean(split[2]);
  }

  dish = clean(dish.replace(/^(?:de|do|da|of|di|du)\s+/iu, ""));
  if (dish.length < 3) return null;
  // Um pedido só de categoria ("melhor restaurante em X") não é um prato.
  if (CARRIER.test(`${dish} `) && !split) return null;

  return {
    dish,
    area: nearMe ? null : area,
    nearMe,
    sweet: SWEET_HINT.test(raw),
  };
}
